import type { ReactNode } from "react";
import logoUrl from "../assets/logo.jpg";
import { GameIcon } from "./GameIcon";
import { fa } from "../i18n/fa";

/**
 * Boot / loading splash — the brand logo (the SAME `assets/logo.jpg` the lobby
 * brand strip uses), the «بینگ گیمز» wordmark in gold, a progress bar and a
 * caption, all on a dark-emerald felt background. Used for the initial load and
 * for "connecting" states. Keeps the `{ caption, progress }?` contract + `--app-h`.
 *
 * `splash` is an optional extra node rendered under the caption — used only by
 * the true app-boot screen to host the SPLASH promo slot (which renders nothing
 * today). Game "connecting" Boots pass none, so they never fetch a promo.
 */
export function Boot({
  caption,
  progress,
  splash,
  onLeave,
  leaveLabel,
  error,
  onRetry,
  retryLabel,
}: {
  caption?: string;
  /** 0..1 → a deterministic staged fill (branded app-boot). Omitted → the
   * indeterminate gold shimmer (game "connecting" screens). */
  progress?: number;
  splash?: ReactNode;
  /** GAME "connecting" screens pass this so the player can back out WHILE the table is still
   * connecting. Without it the splash is a dead end — you had to wait for the table to finish
   * loading before any exit existed, which read as the game being stuck. The true app-boot
   * screen omits it (there is nowhere to go back to). */
  onLeave?: () => void;
  leaveLabel?: string;
  /**
   * A load that has exhausted its retries — replaces the progress bar with an explanation and a
   * «تلاش مجدد» button. Used when a card table's artwork chunk cannot be fetched: the player is
   * already committed to a game, so the one thing they must never get is a silent stall or a
   * table dealt with blank cards. See `game/card-faces.ts`.
   */
  error?: string;
  onRetry?: () => void;
  retryLabel?: string;
}): ReactNode {
  return (
    <div
      style={{
        position: "relative",
        height: "var(--app-h)",
        display: "grid",
        placeItems: "center",
        background:
          "radial-gradient(130% 120% at 50% 30%, var(--felt-2) 0%, var(--felt-edge) 55%, var(--boot-vignette) 100%)",
        color: "var(--text)",
      }}
    >
      {onLeave ? (
        <button
          type="button"
          onClick={onLeave}
          aria-label={leaveLabel ?? "خروج"}
          data-testid="boot-leave"
          className="appearance-none m-0 cursor-pointer flex h-10 w-10 flex-none items-center justify-center rounded-full border border-outline-variant bg-surface-container/80 text-on-surface-variant shadow-md transition-transform hover:text-error active:scale-95"
          style={{
            position: "absolute",
            insetInlineStart: 16,
            top: "calc(10px + max(env(safe-area-inset-top), var(--tg-safe-top, 0px)))",
            zIndex: 2,
          }}
        >
          <GameIcon type="exit" size={20} />
        </button>
      ) : null}
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 24 }}>
        {/* Brand logo — the SAME asset the lobby brand strip uses (assets/logo.jpg). */}
        <img
          src={logoUrl}
          alt=""
          style={{
            width: 96,
            height: 96,
            borderRadius: 24,
            objectFit: "contain",
            filter: "drop-shadow(0 0 8px rgba(199,154,63,0.5))",
            animation: "bg-float 3s ease-in-out infinite",
          }}
        />

        {/* Wordmark. */}
        <h1
          style={{
            margin: 0,
            fontSize: 28,
            fontWeight: 800,
            letterSpacing: 0.5,
            color: "var(--accent)",
            textAlign: "center",
          }}
        >
          {fa.boot.wordmark}
        </h1>

        {/* Progress bar: a deterministic staged fill when `progress` is supplied
            (the branded app-boot splash), else the indeterminate gold shimmer
            (game "connecting" screens keep the old behaviour). Hidden once a load has
            given up — a bar still creeping beside an error reads as "still trying". */}
        {error ? null : (
          <div
            style={{
              width: 192,
              height: 4,
              borderRadius: 999,
              overflow: "hidden",
              background: "var(--score-chip-bg)",
              marginTop: 4,
            }}
          >
            {typeof progress === "number" ? (
              <div
                aria-label="boot-progress"
                style={{
                  height: "100%",
                  width: `${Math.round(Math.max(0, Math.min(1, progress)) * 100)}%`,
                  borderRadius: 999,
                  background: "var(--accent)",
                  transition: "width .45s cubic-bezier(.4,0,.2,1)",
                }}
              />
            ) : (
              <div
                style={{
                  height: "100%",
                  width: "45%",
                  borderRadius: 999,
                  background: "linear-gradient(90deg,transparent,var(--accent),transparent)",
                  backgroundSize: "200% 100%",
                  animation: "bg-shimmer 1.3s linear infinite",
                }}
              />
            )}
          </div>
        )}

        <div
          style={{
            fontSize: 13,
            color: error ? "var(--danger)" : "var(--hint)",
            whiteSpace: "pre-line",
            lineHeight: 1.7,
            textAlign: "center",
            maxWidth: 280,
          }}
          role={error ? "alert" : undefined}
        >
          {error ?? caption ?? fa.status.loading}
        </div>

        {error && onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            style={{
              appearance: "none",
              border: "1px solid var(--accent)",
              background: "transparent",
              color: "var(--accent)",
              borderRadius: 999,
              padding: "8px 22px",
              fontSize: 14,
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            {retryLabel ?? fa.status.retry}
          </button>
        ) : null}

        {/* Optional SPLASH promo host (renders nothing today). Constrained so a
            future campaign card doesn't overflow the centered boot column. */}
        {splash ? <div style={{ width: 260, maxWidth: "86vw" }}>{splash}</div> : null}
      </div>
    </div>
  );
}
